/*───────────────────────────────────────────────────────────────────────────*\
  File        : app/meridian/MeridianWorkshopRequestClient.tsx
  Description : Client-side Meridian workshop request panel. Opens from the
                landing-page workshop CTA, submits visitor details, and sends
                the request through the Meridian workshop request endpoint.
\*───────────────────────────────────────────────────────────────────────────*/

"use client"

import type { ChangeEvent, FormEvent } from "react"
import { useCallback, useEffect, useState } from "react"

interface WorkshopRequestResponse {
  readonly success?: boolean
  readonly error?: string
}

export default function MeridianWorkshopRequestClient() {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [name, setName] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [organization, setOrganization] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false)

  const openRequest = useCallback((): void => {
    setIsOpen(true)
    setErrorMessage("")
    setRequestSubmitted(false)
  }, [])

  const closeRequest = useCallback((): void => {
    setIsOpen(false)
    setErrorMessage("")
  }, [])

  useEffect((): (() => void) => {
    const triggers: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(
      "[data-meridian-workshop-trigger]",
    )

    const handleTriggerClick = (event: Event): void => {
      event.preventDefault()
      openRequest()
    }

    for (let index = 0; index < triggers.length; index += 1) {
      const trigger: HTMLElement = triggers[index]
      trigger.addEventListener("click", handleTriggerClick)
    }

    return (): void => {
      for (let index = 0; index < triggers.length; index += 1) {
        const trigger: HTMLElement = triggers[index]
        trigger.removeEventListener("click", handleTriggerClick)
      }
    }
  }, [openRequest])

  useEffect((): (() => void) | undefined => {
    if (!isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closeRequest()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return (): void => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [closeRequest, isOpen])

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value)
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value)
  }

  const handleOrganizationChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setOrganization(event.target.value)
  }

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(event.target.value)
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    setErrorMessage("")

    const trimmedName: string = name.trim()
    const trimmedEmail: string = email.trim()
    const trimmedOrganization: string = organization.trim()
    const trimmedMessage: string = message.trim()

    if (!trimmedName || !trimmedEmail) {
      setErrorMessage("Please enter your name and email address.")
      return
    }

    setIsSubmitting(true)

    try {
      const response: Response = await fetch("/api/meridian/workshop-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          organization: trimmedOrganization,
          message: trimmedMessage,
          pagePath: window.location.pathname,
          submittedAt: new Date().toISOString(),
        }),
      })
      const data: WorkshopRequestResponse = await response.json()

      if (!response.ok || !data.success) {
        setErrorMessage(data.error ?? "The request could not be submitted.")
        return
      }

      setRequestSubmitted(true)
    } catch {
      setErrorMessage("The request could not be submitted.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="meridian-pdf-preview" role="dialog" aria-modal="true" aria-labelledby="meridian-workshop-request-title">
      <button
        aria-label="Close workshop request"
        className="meridian-pdf-preview__backdrop"
        onClick={closeRequest}
        type="button"
      />
      <section className="meridian-pdf-preview__panel meridian-workshop-request__panel">
        <div className="meridian-pdf-preview__header">
          <div>
            <span className="meridian-pdf-preview__eyebrow">RBCG workshop request</span>
            <h2 id="meridian-workshop-request-title">Talk to RBCG about a workshop</h2>
            <p>
              Send your contact details and RBCG will follow up about a Meridian Model workshop.
            </p>
          </div>
          <button
            aria-label="Close workshop request"
            className="meridian-pdf-preview__close"
            onClick={closeRequest}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="meridian-pdf-preview__body meridian-workshop-request__body">
          <aside className="meridian-pdf-preview__request meridian-workshop-request__request">
            {requestSubmitted ? (
              <div className="meridian-pdf-preview__success">
                <strong>Request received.</strong>
                <span>RBCG will follow up by email.</span>
                <button onClick={closeRequest} type="button">
                  Close
                </button>
              </div>
            ) : (
              <form className="meridian-pdf-preview__form" onSubmit={handleSubmit}>
                <label>
                  <span>Name</span>
                  <input
                    autoComplete="name"
                    name="name"
                    onChange={handleNameChange}
                    required
                    type="text"
                    value={name}
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input
                    autoComplete="email"
                    name="email"
                    onChange={handleEmailChange}
                    required
                    type="email"
                    value={email}
                  />
                </label>
                <label>
                  <span>Organization optional</span>
                  <input
                    autoComplete="organization"
                    name="organization"
                    onChange={handleOrganizationChange}
                    type="text"
                    value={organization}
                  />
                </label>
                <label>
                  <span>Message optional</span>
                  <textarea
                    name="message"
                    onChange={handleMessageChange}
                    value={message}
                  />
                </label>
                {errorMessage ? (
                  <p className="meridian-pdf-preview__error">{errorMessage}</p>
                ) : null}
                <button className="btn--light" disabled={isSubmitting} type="submit">
                  {isSubmitting ? "Submitting..." : "Send Workshop Request"}
                </button>
              </form>
            )}
          </aside>
        </div>
      </section>
    </div>
  )
}

/*──────────────────────────── End of file ────────────────────────────────*/
